import React, { useState, useEffect } from "react";
import AdminLayout from "../components/AdminLayout";
import MetricCard from "../components/MetricCard";
import SubmissionsPieChart from "../components/SubmissionsPieChart";
import DataSection from "../components/DataSection";
import EmailOutreach from "../components/EmailOutreach";
import UserManagement from "../components/UserManagement";
import ProgressIndicator from "../common/ProgressIndicator";
import EmailService from "../services/EmailService";
import { UserPlus, MessageSquare, Bell } from "lucide-react";

const AdminDashboard = () => {
  const [earlyAccessData, setEarlyAccessData] = useState([]);
  const [contactFormData, setContactFormData] = useState([]);
  const [updatesSignupData, setUpdatesSignupData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progressText, setProgressText] = useState("Initializing...");

  const SHEETS_API_KEY = process.env.REACT_APP_PROJECT_ID;
  const CONTACT_SHEET_ID = process.env.REACT_APP_CONTACT_SHEET_ID;
  const EARLY_ACCESS_SHEET_ID = process.env.REACT_APP_EARLY_ACCESS_SHEET_ID;
  const SIGNUP_UPDATE_SHEET_ID = process.env.REACT_APP_SIGNUP_UPDATE_SHEET_ID;

  useEffect(() => {
    fetchSheetData();
  }, []);

  const fetchSheetData = async () => {
    const fetchGoogleSheet = async (sheetId, range) => {
      try {
        const response = await fetch(
          `https://sheets.googleapis.com/v4/spreadsheets/${sheetId}/values/${range}?key=${SHEETS_API_KEY}`
        );
        if (!response.ok) throw new Error(`Error ${response.status}`);
        const data = await response.json();
        return data.values?.slice(1) || [];
      } catch (error) {
        console.error(`Error fetching data: ${error.message}`);
        return [];
      }
    };

    setEarlyAccessData(
      await fetchGoogleSheet(EARLY_ACCESS_SHEET_ID, "sheet1!A1:E")
    );
    setContactFormData(await fetchGoogleSheet(CONTACT_SHEET_ID, "sheet1!A1:E"));
    setUpdatesSignupData(
      await fetchGoogleSheet(SIGNUP_UPDATE_SHEET_ID, "sheet1!A1:C")
    );
  };

  const sendOutEmails = async (
    emails,
    companyName = "ZetaFlow Team",
    emailData
  ) => {
    setLoading(true);
    setProgressText("Started sending out email messages...");

    try {
      await Promise.all(
        emails.map(async (email) => {
          try {
            const emailRequest = {
              ToEmail: email,
              Subject:
                emailData.title || "Invitation to ZetaFlow Productivity App",
              Content: `
                <html>
                  <body>
                    <p>Dear ${email},</p>
                    <p>You have been invited to the <strong>ZetaFlow Productivity App</strong></p>
                    <p>by the ${companyName}.</strong></p>
                    <p>${emailData.message}</p>
                  </body>
                </html>
              `,
            };

            await EmailService.sendEmail(emailRequest);
          } catch (error) {
            console.error(`Error sending invitation to ${email}:`, error);
            setLoading(false);
            throw error;
          }
        })
      );

      setProgressText("Emails sent successfully");
      setLoading(false);
      return true;
    } catch (error) {
      setProgressText("Error sending Emails");
      setLoading(false);
      console.errr("Error sending Emails:", error);
      throw error;
    }
  };

  const handleSendEmail = async (emailData) => {
    // Implement email sending logic here
    console.log("Sending email from AdminDashboard:", emailData);
    try {
      await sendOutEmails(
        emailData?.recipientEmails,
        "ZetaFlow Team",
        emailData
      );
      console.log("Emails sent successfully");
      return true;
    } catch (error) {
      console.error("Error sending emails:", error);
      return false;
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9FF] overflow-x-hidden">
      <div className="max-w-full mx-auto px-4 py-8">
        <h1 className="text-[#0E0444] mb-8 text-2xl sm:text-3xl md:text-4xl font-semibold">
          Admin Dashboard
        </h1>

        {/* Metric Cards Section */}
        <div className="mb-6 md:mb-8 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 px-2 sm:px-0 max-w-full">
          <MetricCard
            title="Early Access Signups"
            value={earlyAccessData.length}
            label="Total Signups"
            icon={UserPlus}
          />
          <MetricCard
            title="Contact Form Submissions"
            value={contactFormData.length}
            label="Total Submissions"
            icon={MessageSquare}
          />
          <MetricCard
            title="Updates Subscribers"
            value={updatesSignupData.length}
            label="Total Subscribers"
            icon={Bell}
          />
        </div>

        {/* Charts Column SubmissionsPieChart*/}
        <div className="mb-8">
          <div className="mx-auto w-full">
            <SubmissionsPieChart
              earlyAccessCount={earlyAccessData.length}
              contactFormCount={contactFormData.length}
              updatesCount={updatesSignupData.length}
            />
          </div>
        </div>

        {/* Email Section Column */}
        <div className="mb-8">
          <div className="w-full">
            <EmailOutreach
              earlyAccessData={earlyAccessData}
              contactFormData={contactFormData}
              updatesSignupData={updatesSignupData}
              onSendEmail={handleSendEmail}
            />
          </div>
        </div>

        {/* Data Tables Section */}
        {/* Data Tables Section */}
        <div className="space-y-6">
          <DataSection
            title="Early Access Signups"
            headers={["Name", "Email", "Company", "Role", "Timestamp"]}
            data={earlyAccessData}
          />

          <DataSection
            title="Contact Form Submissions"
            headers={[
              "Timestamp",
              "Full Name",
              "Email",
              "Inquiry Type",
              "Message",
            ]}
            data={contactFormData}
          />

          <DataSection
            title="Updates Signups"
            headers={["Timestamp", "Name", "Email"]}
            data={updatesSignupData}
          />
        </div>
      </div>
      <ProgressIndicator text={progressText} isLoading={loading} />
    </div>
  );
};

export default AdminDashboard;
